import React from 'react';
import { ClipLoader } from 'react-spinners';

import AddImage from '../../assets/add-photo.webp';
import { IPortfolioItem } from '../../mock-data/portfolioData';
import type { ICompliancePropertyStatus } from '../../services/zeos-api/contracts/Compliance';
import type { IIncentivePropertySignals } from '../../services/zeos-api/contracts/Incentive';
import { CarbonClearGovEnum } from '../../services/zeos-api/enums/AutoCompliance';
import { ZeosActionButton } from '../Shared/ZeosActionButton';
import { SignalChips } from './SignalChips';
import { buildSignalChips } from './signalProviders';

interface IProps {
  /** Per-property compliance from Compliance/Portfolio; undefined until loaded (or if the call failed). */
  complianceStatus?: ICompliancePropertyStatus | undefined;
  disabled?: boolean;
  /** Per-property incentive signals from Incentive/Signals/Portfolio; undefined until loaded. */
  incentiveSignals?: IIncentivePropertySignals | undefined;
  /** True while the incentive signals call is in flight, so the chip says "Checking…" instead of "No data". */
  incentiveSignalsLoading?: boolean;
  handleClick: (property: IPortfolioItem) => void;
  loadingProperty: string | null;
  offerCarbonClear?: boolean | undefined;
  onEditProperty?: (property: IPortfolioItem) => void;
  onLinkUtility: (property: IPortfolioItem) => void;
  /** Opens the property's Incentives page (task-4110). */
  onOpenIncentives?: (property: IPortfolioItem) => void;
  onOpenCarbonClearModal?: (property: IPortfolioItem) => void;
  /** Set only when the user may manage this property's meters (task-4062). */
  onOpenMeters?: (property: IPortfolioItem) => void;
  onUploadImage?: (property: IPortfolioItem) => void;
  property: IPortfolioItem;
  carbonClearStatus: CarbonClearGovEnum | undefined;
}

export const PortfolioTile = (props: IProps) => {
  const isLoading = props.loadingProperty === props.property.name;
  const hasImage = !!props.property.image;

  const onTileClick = () => {
    if (props.disabled) return;
    props.handleClick(props.property);
  };

  const contentImage = () => {
    if (hasImage) {
      return (
        <img
          src={props.property.image!}
          className="w-full h-[150px] object-cover rounded-md"
          alt={props.property.name}
        />
      );
    } else {
      return (
        <div className="flex items-center justify-center bg-white rounded-md border border-2 border-gray-400 h-[150px]">
          <div className="flex items-center space-x-4">
            <img
              className="w-30 h-20 text-gray-500 relative top-[-8px]"
              src={AddImage}
              alt=""
            />
          </div>
        </div>
      );
    }
  };

  const contentTileButton = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-[150px]">
          <ClipLoader color="#f97316" size={30} />
        </div>
      );
    } else {
      return contentImage();
    }
  };

  return (
    <div className="space-y-2 ">
      <div
        role="button"
        className={`space-y-2 transition rounded-md
        ${props.disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
        hover:scale-105
        focus:scale-105 focus:div-1
        active:scale-95
      `}
        onClick={onTileClick}
      >
        <h3 className="text-lg text-left font-medium whitespace-nowrap overflow-hidden text-ellipsis">
          {props.property.name}
        </h3>
        <div className="relative block">{contentTileButton()}</div>
      </div>
      <div>
        <div className="flex flex-wrap gap-2">
          <ZeosActionButton
            label="Edit"
            onClick={(e) => {
              e.stopPropagation();
              props.onEditProperty?.(props.property);
            }}
          />
          {props.onUploadImage && (
            <ZeosActionButton
              label="Upload Image"
              onClick={(e) => {
                e.stopPropagation();
                props.onUploadImage?.(props.property);
              }}
            />
          )}
          <ZeosActionButton
            label="Link Utility"
            onClick={(e) => {
              e.stopPropagation();
              props.onLinkUtility(props.property);
            }}
          />
          {props.onOpenMeters && (
            <ZeosActionButton
              label="Meters"
              onClick={(e) => {
                e.stopPropagation();
                props.onOpenMeters?.(props.property);
              }}
            />
          )}
          {props.carbonClearStatus === CarbonClearGovEnum.InProgress ? (
            <div className="px-3 py-2 text-xs font-medium text-gray-500">
              cc in progress
            </div>
          ) : (
            props.offerCarbonClear &&
            props.onOpenCarbonClearModal && (
              <ZeosActionButton
                label="Carbon Clear"
                onClick={(e) => {
                  e.stopPropagation();
                  props.onOpenCarbonClearModal?.(props.property);
                }}
              />
            )
          )}
        </div>
        {/* Signal chips replace Revenue + Profitability (task-4035). The project financials stay on IPortfolioItem. */}
        <SignalChips
          chips={buildSignalChips({
            compliance: props.complianceStatus,
            incentives: props.incentiveSignals,
            incentivesLoading: props.incentiveSignalsLoading,
          })}
          onChipAction={(action) => {
            if (action === "incentives") props.onOpenIncentives?.(props.property);
          }}
        />
      </div>
    </div>
  );
};
